import type { ProjectListItem, SeriesDto } from '@dispositio/shared';
import { lazy, Suspense, useState, type DragEvent, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { useAuthMutations, useProjectMutations, useProjects, useSeries, useSeriesMutations } from '../api/hooks';
import { BrandLockup } from '../components/BrandLogo';
import { EpisodeReferences } from '../components/EpisodeReferences';
import { Chevron } from '../components/editor/BinderIcon';
import { Modal } from '../components/Modal';
import { ProfileLink } from '../components/ProfileLink';
import { SiteFooter } from '../components/SiteFooter';
import { de } from '../i18n/de';
import { onDeleteKey } from '../lib/deleteKey';
import { relativeTime } from '../lib/relativeTime';
import { startGuide } from '../lib/tutorialGuide';
import { TUTORIALS, tutorialProject, tutorialState, type TutorialInfo } from '../lib/tutorials';
import { toast } from '../state/toast';
import { startInManuscript } from '../state/workspace';
import { requestBeatSetup } from '../state/beatSetup';

const P = de.projects;

// Einstellungen einer Geschichte erst bei Bedarf laden (Typen-Editor), damit die Übersicht schlank bleibt
const StorySettingsModal = lazy(() => import('../components/StorySettings').then((m) => ({ default: m.StorySettingsModal })));

/** Medientyp, unter dem eine Geschichte beim Ziehen in eine andere Serie übergeben wird */
const PROJECT_MIME = 'application/x-dispositio-project';
/** Zugeklappte Gruppen der Übersicht (`tutorials` bzw. die ID einer Serie), gemerkt im Browser */
const CLOSED_KEY = 'dispositio.overview.closed';
const TUTORIALS_KEY = 'tutorials';

function readClosed(): Set<string> {
  try {
    const raw = window.localStorage.getItem(CLOSED_KEY);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}
function writeClosed(closed: ReadonlySet<string>): void {
  try {
    window.localStorage.setItem(CLOSED_KEY, JSON.stringify([...closed]));
  } catch {
    // ohne Speicher: gilt bis zum Neuladen
  }
}

/** Fünf Töne für die Cover, fest je Geschichte (aus ihrer ID) */
const COVERS = 5;
function coverOf(id: string): number {
  let h = 0;
  for (const c of id) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  return h % COVERS;
}

/** Anfangsbuchstabe fürs Cover – ohne Artikel am Anfang („Die Insel“ → „I“) */
function monogram(name: string): string {
  const words = name.trim().split(/\s+/);
  const first = words.length > 1 && /^(der|die|das|ein|eine|the|a|an)$/i.test(words[0]!) ? words[1]! : (words[0] ?? '');
  return (first.match(/\p{L}|\p{N}/u)?.[0] ?? '·').toUpperCase();
}

/** Aufgeschlagenes Buch: Symbol der Tutorials (24er Raster wie die Symbole der App) */
function TutorialIcon() {
  return (
    <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2.5 5.5c2.6-1.1 5.6-1 8.2.6v13c-2.6-1.6-5.6-1.7-8.2-.6zM21.5 5.5c-2.6-1.1-5.6-1-8.2.6v13c2.6-1.6 5.6-1.7 8.2-.6z" />
    </svg>
  );
}

/** Einstellungen: drei Regler */
function SettingsIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="17" cy="18" r="2" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" />
    </svg>
  );
}

/**
 * Eine Geschichte als Buchcover: getönte Fläche mit Buchrücken und Anfangsbuchstabe, darunter Titel, Umfang und letzte Bearbeitung.
 * Lässt sich in eine andere Serie ziehen; in ihrer Serie trägt sie ihre Nummer als Episode, und ein anderes Cover, das man auf sie zieht,
 * nimmt ihren Platz ein (`onDropStory`).
 */
function StoryCard({ project, onSettings, onDelete, onDropStory }: { project: ProjectListItem; onSettings: () => void; onDelete: () => void; onDropStory?: (projectId: string) => void }) {
  const [dragging, setDragging] = useState(false);
  const [over, setOver] = useState(false);
  const accepts = (e: DragEvent): boolean => !!onDropStory && !dragging && e.dataTransfer.types.includes(PROJECT_MIME);
  return (
    <article
      className={`story-card${dragging ? ' dragging' : ''}${over ? ' drop-here' : ''}`}
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(PROJECT_MIME, project.id);
        e.dataTransfer.setData('text/plain', project.name);
        e.dataTransfer.effectAllowed = 'move';
        setDragging(true);
      }}
      onDragEnd={() => setDragging(false)}
      onKeyDown={onDeleteKey(onDelete)}
      onDragOver={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        const id = e.dataTransfer.getData(PROJECT_MIME);
        if (id && id !== project.id) onDropStory!(id);
      }}
      data-testid="story-card"
      data-project-id={project.id}
      data-episode={project.episode ?? undefined}
    >
      <Link className="story-card-link" to={`/p/${project.id}`} draggable={false}>
        <div className={`story-cover v${coverOf(project.id)}`} aria-hidden="true">
          <span className="story-monogram">{monogram(project.name)}</span>
        </div>
        <div className="story-body">
          {project.episode !== null && (
            <div className="story-episode" title={P.episodeHint} data-testid="story-episode">
              {P.episode(project.episode)}
            </div>
          )}
          <h3 className="story-title">{project.name}</h3>
          <div className="story-meta">
            {P.nodes(project.nodeCount)} · {P.edited(relativeTime(project.updatedAt))}
          </div>
          {project.folder && <div className="story-meta">{P.linked(project.folder)}</div>}
        </div>
      </Link>
      <div className="story-actions">
        <button type="button" className="story-action" onClick={onSettings} aria-label={P.settingsLabel(project.name)} title={P.settingsLabel(project.name)} data-testid="story-settings-open">
          <SettingsIcon />
        </button>
        {/* Projekt-Sicherung als ZIP (PLAN.md §29): der Server liefert sie als Download */}
        <a className="story-action" href={`/api/projects/${project.id}/backup.zip`} download aria-label={P.backupLabel(project.name)} title={P.backupLabel(project.name)} data-testid="story-backup">
          <DownloadIcon />
        </a>
        <button type="button" className="story-action danger" onClick={onDelete} aria-label={P.deleteLabel(project.name)} title={P.deleteLabel(project.name)}>
          <TrashIcon />
        </button>
      </div>
    </article>
  );
}

/** Ein Tutorial als Cover: öffnen (beim ersten Mal entsteht es, danach geht es weiter, wo man war) oder neu beginnen. */
function TutorialCard({ tutorial, project, busy, onOpen, onRestart }: { tutorial: TutorialInfo; project: ProjectListItem | null; busy: boolean; onOpen: () => void; onRestart: () => void }) {
  const state = tutorialState(project, tutorial.steps);
  const meta = state.kind === 'new' ? tutorial.intro : state.kind === 'step' ? P.tutorialStep(state.step, tutorial.steps) : P.edited(relativeTime(project!.updatedAt));
  return (
    <article className="story-card tutorial-card" data-testid="tutorial-card" data-tutorial={tutorial.key}>
      <button type="button" className="story-card-link" onClick={onOpen} disabled={busy} title={project ? P.tutorialOpenHint(tutorial.title) : P.tutorialStartHint(tutorial.title)} data-testid="open-demo">
        <div className="story-cover tutorial" aria-hidden="true">
          <TutorialIcon />
        </div>
        <div className="story-body">
          <h3 className="story-title">{tutorial.title}</h3>
          <div className="story-meta" data-testid="tutorial-meta">
            {meta}
          </div>
        </div>
      </button>
      {project && (
        <button type="button" className="btn ghost small tutorial-restart" onClick={onRestart} disabled={busy} title={P.tutorialRestartHint} data-testid="tutorial-restart">
          ↺ {P.tutorialRestart}
        </button>
      )}
    </article>
  );
}

interface ShelfProps {
  title: string;
  count: number;
  open: boolean;
  onToggle: () => void;
  /** Serien: umbenennen, löschen (`null`: die letzte, bleibt), Geschichten hineinziehen */
  onRename?: (name: string) => void;
  onDelete?: (() => void) | null;
  onDropStory?: (projectId: string) => void;
  testId: string;
  seriesId?: string;
  children: ReactNode;
}

/** Eine Gruppe der Übersicht (Tutorials, eine Serie): Kopf mit Name, Anzahl und Werkzeugen, aufklappbar. */
function Shelf({ title, count, open, onToggle, onRename, onDelete, onDropStory, testId, seriesId, children }: ShelfProps) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(title);
  const [over, setOver] = useState(false);
  const rename = (): void => {
    setName(title);
    setEditing(true);
  };
  const commit = (): void => {
    setEditing(false);
    const next = name.trim();
    if (next && next !== title) onRename?.(next);
  };
  const accepts = (e: DragEvent): boolean => !!onDropStory && e.dataTransfer.types.includes(PROJECT_MIME);
  return (
    <section
      className={`shelf${over ? ' over' : ''}${open ? '' : ' closed'}`}
      aria-label={title}
      data-testid={testId}
      data-series-id={seriesId}
      onDragOver={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={(e) => {
        if (!accepts(e)) return;
        e.preventDefault();
        setOver(false);
        const id = e.dataTransfer.getData(PROJECT_MIME);
        if (id) onDropStory!(id);
      }}
    >
      <header className="shelf-head" onKeyDown={onDeleteKey(editing ? null : onDelete)}>
        <Chevron open={open} onToggle={onToggle} label={open ? P.collapse(title) : P.expand(title)} />
        {editing ? (
          <input
            autoFocus
            className="shelf-rename"
            value={name}
            aria-label={P.seriesName}
            maxLength={120}
            onChange={(e) => setName(e.target.value)}
            onFocus={(e) => e.currentTarget.select()}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commit();
              if (e.key === 'Escape') {
                e.stopPropagation();
                setName(title);
                setEditing(false);
              }
            }}
            data-testid="shelf-rename-input"
          />
        ) : (
          <button type="button" className="shelf-title" onClick={onToggle} onDoubleClick={onRename ? rename : undefined} title={onRename ? P.seriesHint : undefined} data-testid="shelf-title">
            {title}
          </button>
        )}
        <span className="shelf-count" data-testid="shelf-count">
          {count}
        </span>
        <span className="grow" />
        {onRename && (
          <span className="shelf-tools">
            <button type="button" className="btn ghost icon small" onClick={rename} title={P.seriesRename} aria-label={`${title}: ${P.seriesRename}`} data-testid="shelf-rename">
              ✎
            </button>
            <button
              type="button"
              className="btn ghost icon small"
              onClick={onDelete ?? undefined}
              disabled={!onDelete}
              title={onDelete ? P.seriesDelete : P.seriesLast}
              aria-label={`${title}: ${onDelete ? P.seriesDelete : P.seriesLast}`}
              data-testid="shelf-delete"
            >
              ✕
            </button>
          </span>
        )}
      </header>
      {open && <div className="shelf-body">{children}</div>}
    </section>
  );
}

/** „+ Neue Serie“: ein Eingabefeld für den Namen; Enter legt sie hinter den anderen an. */
function NewSeries({ onCreate }: { onCreate: (name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  if (!editing) {
    return (
      <button
        type="button"
        className="btn ghost shelf-new"
        onClick={() => {
          setName(P.newSeriesDefault);
          setEditing(true);
        }}
        data-testid="series-new"
      >
        {P.newSeries}
      </button>
    );
  }
  const commit = (): void => {
    setEditing(false);
    const next = name.trim();
    if (next) onCreate(next);
  };
  return (
    <div className="shelf-new-row">
      <input
        autoFocus
        className="shelf-rename"
        value={name}
        aria-label={P.seriesName}
        maxLength={120}
        onChange={(e) => setName(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            e.stopPropagation();
            setEditing(false);
          }
        }}
        data-testid="series-new-name"
      />
    </div>
  );
}

/**
 * Übersicht nach der Anmeldung: oben die Tutorials („Rundgang“ u. a.), darunter die Serien des Kontos mit ihren Geschichten (als Episoden in
 * ihrer Reihenfolge, PLAN.md §43) und je einer Karte „Neue Geschichte anlegen“ – in einer Serie mit Geschichten danach das Fenster „Aus
 * früheren Episoden übernehmen“. Gruppen klappen auf und zu; Geschichten lassen sich in eine andere Serie ziehen bzw. umsortieren. Ein
 * neues Konto hat „Meine Serie“.
 */
export function ProjectsPage() {
  const projectsQ = useProjects();
  const seriesQ = useSeries();
  const { create, remove, startTutorial: start, importBackup } = useProjectMutations();
  const seriesMut = useSeriesMutations();
  const { logout } = useAuthMutations();
  const nav = useNavigate();
  /** Serie, in der gerade eine Geschichte angelegt wird (Fenster offen) */
  const [creatingIn, setCreatingIn] = useState<SeriesDto | null>(null);
  /** Schritt beim Anlegen: Name, dann (in einer Serie mit Geschichten) die Referenzen */
  const [step, setStep] = useState<'name' | 'refs'>('name');
  const [name, setName] = useState('');
  /** Geschichte, deren Einstellungen offen sind */
  const [settingsOf, setSettingsOf] = useState<ProjectListItem | null>(null);
  const [closed, setClosed] = useState<ReadonlySet<string>>(readClosed);

  const projects = projectsQ.data;
  const series = seriesQ.data;
  const stories = (projects ?? []).filter((p) => !TUTORIALS.some((t) => t.template === p.template));
  // Geschichten ohne Serie (gerade erst angelegt, noch nicht neu geladen) stehen in der ersten
  const seriesOf = (p: ProjectListItem): string | undefined => p.seriesId ?? series?.[0]?.id;
  const failed = (err: unknown): void => {
    toast.error(errorMessage(err));
  };

  const toggle = (key: string): void =>
    setClosed((cur) => {
      const next = new Set(cur);
      if (!next.delete(key)) next.add(key);
      writeClosed(next);
      return next;
    });
  /** Neue Geschichte: in einer Serie mit Geschichten erst das Fenster „Aus früheren Episoden übernehmen“ (PLAN.md §43) */
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (creatingIn && stories.some((p) => seriesOf(p) === creatingIn.id)) setStep('refs');
    else createStory([]);
  };
  const createStory = (references: string[]): void => {
    create.mutate(
      { name, ...(creatingIn ? { seriesId: creatingIn.id } : {}), ...(references.length > 0 ? { references } : {}) },
      {
        onSuccess: (p) => {
          startInManuscript(p.id);
          // die neue Geschichte fragt gleich nach ihren Story Beats
          requestBeatSetup(p.id);
          nav(`/p/${p.id}`);
        },
        onError: failed,
      },
    );
  };
  /** frisches Projekt des Tutorials; mit Führung beginnt sie gleich */
  const startTutorial = (t: TutorialInfo): void => {
    // das neue Projekt ersetzt das bisherige des Tutorials: dessen Stand der Führung fällt weg
    const previous = tutorialProject(t, projects ?? [])?.id ?? null;
    start.mutate(t.path, {
      onSuccess: (p) => {
        if (t.steps > 0) startGuide(p.id, previous);
        nav(`/p/${p.id}`);
      },
      onError: failed,
    });
  };
  const openTutorial = (t: TutorialInfo): void => {
    const existing = tutorialProject(t, projects ?? []);
    if (existing) nav(`/p/${existing.id}`);
    else startTutorial(t);
  };
  const restartTutorial = (t: TutorialInfo): void => {
    if (confirm(P.tutorialRestartConfirm(t.title))) startTutorial(t);
  };
  const removeProject = (p: ProjectListItem): void => {
    if (confirm(P.deleteConfirm(p.name))) remove.mutate(p.id, { onError: failed });
  };
  const removeSeries = (s: SeriesDto, count: number): void => {
    if (count > 0 && !confirm(P.seriesDeleteConfirm(s.name, count))) return;
    seriesMut.remove.mutate({ id: s.id, withStories: count > 0 }, { onError: failed });
  };
  /** Projekt-Sicherung (ZIP) als neue Geschichte einlesen */
  const restore = (file: File): void =>
    importBackup.mutate(file, {
      onSuccess: (p) => toast.action(P.restored(p.name), P.open, () => nav(`/p/${p.id}`)),
      onError: failed,
    });
  const moveStory = (projectId: string, to: SeriesDto): void => {
    const p = stories.find((x) => x.id === projectId);
    if (!p || seriesOf(p) === to.id) return;
    seriesMut.move.mutate({ projectId, seriesId: to.id }, { onError: failed });
  };
  /** Ein Cover auf ein anderes derselben Serie gezogen: es nimmt dessen Platz ein (aus einer anderen Serie: dorthin verschieben) */
  const placeStory = (projectId: string, target: ProjectListItem, s: SeriesDto, list: ProjectListItem[]): void => {
    if (!list.some((p) => p.id === projectId)) return moveStory(projectId, s);
    const ids = list.map((p) => p.id);
    const next = ids.filter((id) => id !== projectId);
    next.splice(ids.indexOf(target.id), 0, projectId);
    seriesMut.order.mutate({ seriesId: s.id, projectIds: next }, { onError: failed });
  };


  const error = projectsQ.error ?? seriesQ.error;
  return (
    <div className="home">
      <div className="home-wrap">
        <header className="home-head">
          <Link to="/" className="landing-brand" aria-label={de.app.name}>
            <BrandLockup />
            <span className="beta-pill">{de.landing.beta}</span>
          </Link>
          <nav className="home-nav" aria-label={P.nav}>
            <ProfileLink />
            <button className="btn ghost" onClick={() => logout.mutate(undefined, { onSuccess: () => nav('/', { replace: true }) })} data-testid="logout">
              {de.auth.logout}
            </button>
          </nav>
        </header>

        <main>
          <section className="home-intro">
            <div>
              <h1>{P.title}</h1>
              {stories.length > 0 && <p className="home-summary">{P.count(stories.length)}</p>}
            </div>
          </section>

          {(projectsQ.isLoading || seriesQ.isLoading) && <div className="muted">{de.common.loading}</div>}
          {error && <div className="error-text">{errorMessage(error)}</div>}

          {projects && series && (
            <div className="shelves">
              <Shelf title={P.tutorials} count={TUTORIALS.length} open={!closed.has(TUTORIALS_KEY)} onToggle={() => toggle(TUTORIALS_KEY)} testId="shelf-tutorials">
                <div className="story-grid">
                  {TUTORIALS.map((t) => (
                    <TutorialCard key={t.key} tutorial={t} project={tutorialProject(t, projects)} busy={start.isPending} onOpen={() => openTutorial(t)} onRestart={() => restartTutorial(t)} />
                  ))}
                </div>
              </Shelf>
              {series.map((s) => {
                // in der Reihenfolge der Episoden
                const list = stories.filter((p) => seriesOf(p) === s.id).sort((a, b) => (a.episode ?? Infinity) - (b.episode ?? Infinity));
                return (
                  <Shelf
                    key={s.id}
                    title={s.name}
                    count={list.length}
                    open={!closed.has(s.id)}
                    onToggle={() => toggle(s.id)}
                    onRename={(next) => seriesMut.rename.mutate({ id: s.id, name: next }, { onError: failed })}
                    onDelete={series.length > 1 ? () => removeSeries(s, list.length) : null}
                    onDropStory={(id) => moveStory(id, s)}
                    testId="shelf-series"
                    seriesId={s.id}
                  >
                    <div className="story-grid">
                      {list.map((p) => (
                        <StoryCard
                          key={p.id}
                          project={p}
                          onSettings={() => setSettingsOf(p)}
                          onDelete={() => removeProject(p)}
                          onDropStory={(id) => placeStory(id, p, s, list)}
                        />
                      ))}
                      <button
                        type="button"
                        className="story-new"
                        onClick={() => {
                          setName('');
                          setStep('name');
                          setCreatingIn(s);
                        }}
                        data-testid="story-new"
                      >
                        <span className="story-new-plus" aria-hidden="true">
                          +
                        </span>
                        {P.newCard}
                      </button>
                    </div>
                  </Shelf>
                );
              })}
              <div className="row shelf-tools">
                <NewSeries onCreate={(n) => seriesMut.create.mutate(n, { onError: failed })} />
                <label className={`btn ghost shelf-new${importBackup.isPending ? ' disabled' : ''}`} title={P.restoreHint} data-testid="backup-restore">
                  {importBackup.isPending ? P.restoring : P.restore}
                  <input
                    type="file"
                    accept=".zip,application/zip"
                    hidden
                    disabled={importBackup.isPending}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      e.target.value = '';
                      if (file) restore(file);
                    }}
                    data-testid="backup-file"
                  />
                </label>
              </div>
            </div>
          )}

        </main>
      </div>
      <SiteFooter />

      {settingsOf && (
        <Suspense fallback={null}>
          <StorySettingsModal projectId={settingsOf.id} name={settingsOf.name} onClose={() => setSettingsOf(null)} />
        </Suspense>
      )}

      {creatingIn && step === 'refs' && (
        <EpisodeReferences seriesId={creatingIn.id} seriesName={creatingIn.name} name={name.trim()} busy={create.isPending} onBack={() => setStep('name')} onCreate={createStory} />
      )}
      {creatingIn && step === 'name' && (
        <Modal
          title={P.new}
          onClose={() => setCreatingIn(null)}
          footer={
            <>
              <button className="btn" onClick={() => setCreatingIn(null)}>
                {de.common.cancel}
              </button>
              <button className="btn primary" form="new-project" type="submit" disabled={!name.trim() || create.isPending}>
                {de.common.create}
              </button>
            </>
          }
        >
          <form id="new-project" className="col" onSubmit={submit}>
            <p className="muted small">{P.newIn(creatingIn.name)}</p>
            <div className="field">
              <label htmlFor="pname">{P.name}</label>
              <input id="pname" type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={120} required />
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
